import { getTemplateId, useTemplateIdentity } from "../templateBuilder/utils/useTemplateIdentify";
import useDiscard, { discardProps } from "../useDiscard";
import useMap from "../utils/useMap";
import useStoreMonitor from "../warehouse/useStoreMonitor";

const toCollection = (sources) => new Set(sources);

const DEFAULT_PROPS = {}

const bindMutationObserver = (() => {
    const storeEvent = useMap();
    useStoreMonitor('bindMutationObserver', storeEvent);
    const createObserverIfNotExist = (root, onAddedNodes, onRemovedNodes) => {
        const observer = new MutationObserver((mutations) => {
            for (let i = 0, len = mutations.length; i < len; i++) {
                if (mutations[i].type !== 'childList') continue;
                if (mutations[i].addedNodes.length > 0) {
                    onAddedNodes(toCollection(mutations[i].addedNodes));
                }
                if (mutations[i].removedNodes.length > 0) {
                    onRemovedNodes(toCollection(mutations[i].removedNodes))
                }
            }
        })
        observer.observe(root, { childList: true, subtree: true });
        return () => {
            observer.disconnect();
        };
    }

    return (_, root) => {
        root = document.body
        // useDiscard(props, () => {
        //     console.log("discard Props", props)
        //     // unsubcribe()
        //     // storeEvent.get(root).delete(props);
        //     // if (storeEvent.get(root).size === 0) {
        //         // storeEvent.delete(root);
        //     // }
        //     // root = null;
        //     // props = null;
        // })
        if (storeEvent.has(root)) {
            return storeEvent.get(root).instance;
        }
        storeEvent.set(root, {
            callbacksAdded: new Set(),
            callbacksRemoved: new Set(),
            instance: {
                unsubcribe: null,
                onDomRendered: (cb) => {
                    if (typeof cb !== 'function') return;
                    const key = Symbol('cbRender')
                    // storeEvent.get(root).callbacksAdded.set(key, cb)
                    storeEvent.get(root).callbacksAdded.add(cb)
                    return () => {
                        // storeEvent.get(root).callbacksAdded.delete(key)
                        storeEvent.get(root).callbacksAdded.delete(cb)
                    }
                },
                onDomDestroy: (cb) => {
                    if (typeof cb !== 'function') return;
                    const key = Symbol('cbDestroy')
                    // storeEvent.get(root).callbacksRemoved.set(key, cb)
                    storeEvent.get(root).callbacksRemoved.add(cb)
                    return () => {
                        storeEvent.get(root).callbacksRemoved.delete(cb)
                    }
                }
            }
        });

        const instance = storeEvent.get(root).instance

        instance.unsubcribe = createObserverIfNotExist(root, (addedNodes) => {
            if (!storeEvent.has(root)) {
                return;
            }
            const callbacks = storeEvent.get(root).callbacksAdded;
            callbacks.forEach((cb) => cb.call(null, addedNodes))

        }, (removedNodes) => {
            if (!storeEvent.has(root)) {
                return;
            }
            const callbacks = storeEvent.get(root).callbacksRemoved;
            callbacks.forEach((cb) => cb.call(null, removedNodes))
        });

        return instance;
    }
})();

/**
 * 
 * @param {Object} props 
 * @param {Node} root 
 */
const useSubscribeDOMRendered_v2 = (() => {
    const queueRender = useMap(),
        queueDomRendered = new Set();

    useStoreMonitor('useSubscribeDOMRendered', queueRender);

    const callRender = (node) => {
        if (!queueRender.has(node)) return;
        const items = queueRender.get(node);
        for (let i = 0, len = items.length; i < len; i++) {
            const { dom, cb, args } = items[i];
            cb.call(null, args);
        }
        queueRender.delete(node);
    }

    const deleteItem = (node) => {
        queueRender.delete(node);
        if ('querySelectorAll' in node) {
            node.querySelectorAll('*').forEach(n => queueRender.delete(n));
        }
    }

    const onRender = (domList) => {
        domList.forEach(node => {
            callRender(node);
            if ('querySelectorAll' in node) {
                node.querySelectorAll('*').forEach(callRender);
            }
            // deleteItem(node);
        })
    }

    const onDestroyed = (domList) => {
        domList.forEach(node => {
            deleteItem(node);
        })
    }

    return (targetDOM, callback, args, props = DEFAULT_PROPS, root = document.body) => {
        if (!props) throw "[onDOMRendered] props is null";
        const newItem = {
            dom: targetDOM,
            cb: callback,
            args
        }
        if (root.contains(targetDOM)) {
            callback.call(null, args);
            return () => { }
        }
        if (!queueRender.has(targetDOM)) {
            queueRender.set(targetDOM, [])
        }
        queueRender.get(targetDOM).push(newItem);
        const { onDomRendered, onDomDestroy } = bindMutationObserver(props, root)
        onDomRendered(onRender)
        onDomDestroy(onDestroyed)

        if (props !== DEFAULT_PROPS) {
            useDiscard(props, () => {
                deleteItem(targetDOM);
            })
        }
        return () => {
            deleteItem(targetDOM);
        }
    }
})();

const useSubscribeDOMDestroyed_v2 = (() => {
    const queueDestroyed = useMap();

    useStoreMonitor('useSubscribeDOMDestroy', queueDestroyed);

    const callRemove = (node) => {
        if (!queueDestroyed.has(node)) return;
        const items = queueDestroyed.get(node);

        while (items.length > 0) {
            const { dom, cb, args } = items.shift();
            cb.call(null, args);
        }
        queueDestroyed.delete(node);
    }

    const deleteItem = (node) => {
        queueDestroyed.delete(node);
        node.querySelectorAll('*').forEach(n => queueDestroyed.delete(n));
    }

    const onDestroy = (domList) => {
        domList.forEach(node => {
            if ('querySelectorAll' in node) {
                node.querySelectorAll('*').forEach(callRemove);
            }
            callRemove(node);
        })
    }

    return (targetDOM, callback, args, props = DEFAULT_PROPS, root = document.body) => {
        if (!props) throw "[onDOMDestroyed] props is null";

        const { onDomDestroy } = bindMutationObserver(props, root)
        onDomDestroy(onDestroy);

        const newItem = {
            dom: targetDOM,
            cb: callback,
            args,
        }
        if (!queueDestroyed.has(targetDOM)) {
            queueDestroyed.set(targetDOM, [])
        }
        queueDestroyed.get(targetDOM).push(newItem);

        if (props !== DEFAULT_PROPS) {
            useDiscard(props, () => {
                if ('querySelectorAll' in targetDOM) {
                    targetDOM.querySelectorAll('*').forEach(callRemove)
                }
                callRemove(targetDOM);
                deleteItem(targetDOM);
            })
        }
        return () => {
            deleteItem(targetDOM);
        }
    }
})();


const useCustomComponentRendered = (() => {
    const pool = useMap();

    const containNode = nodeName => pool.has(nodeName)

    const observer = new MutationObserver(function (mutations) {
        for (let i = 0; i < mutations.length; i++) {
            const target = mutations[i];
            const { type } = target;
            const addedNodes = toCollection(target.addedNodes),
                removedNodes = toCollection(target.removedNodes);
            if (type !== 'childList') continue;
            if (addedNodes.size > 0) {

                addedNodes.forEach(node => {
                    // if (!node instanceof HTMLScriptElement) {
                    // console.log("node", node)
                    // }
                    if (containNode(node.nodeName)) {
                        const { name, cb } = pool.get(node.nodeName);
                        cb.call(null, { name, node });
                    }
                })
            }

            if (removedNodes.size > 0) {

            }
        }
    });

    const tryEmitIfExisted = (name, cb) => {
        const nodes = Array.from(document.querySelectorAll(name));
        for (let i in nodes) {
            const node = nodes[i];
            cb.call(null, { name, node });
        }
    }

    observer.observe(document.body, { childList: true, subtree: true });

    return (name) => ({
        onCustomComponentRendered: (cb) => {
            if (typeof cb !== 'function') {
                throw new Error("Only allow function arg");
            }
            const callbacks = pool.get(name) || [];
            callbacks.push({
                name,
                cb
            });
            pool.set(name, callbacks);
            tryEmitIfExisted(name, cb)
        }
    })
})()


const [useSubscribeDOMRendered_1, useSubscribeDOMDestroyed_1] = (() => {

    const queueRender = useMap(),
        queueDestroy = useMap(),
        queueDomRendered = new Set();

    const canRemove = (removedNodeList, dom) => removedNodeList.has(dom) && (queueDomRendered.has(dom) || document.body.contains(dom));
    const observer = new MutationObserver(function (mutations) {

        for (let i = 0; i < mutations.length; i++) {
            const target = mutations[i];
            const { type } = target;
            const addedNodes = toCollection(target.addedNodes),
                removedNodes = toCollection(target.removedNodes);
            if (type !== 'childList') continue;

            if (addedNodes.size > 0) {

                addedNodes.forEach(node => {

                    const items = queueRender.get(node);

                    if (items) {
                        items.forEach(({ cb, args }) => {
                            cb.call(null, args);
                        })
                    }
                    if (node.querySelectorAll) {
                        node.querySelectorAll("*").forEach((current) => {
                            const items = queueRender.get(current);
                            if (items) {
                                items.forEach(({ cb, args }) => {
                                    cb.call(null, args);
                                })
                            }
                        })
                    }
                    // queueRender.delete(node);
                    // queueDomRendered.add(node);
                })
            }

            if (removedNodes.size > 0) {

                removedNodes.forEach(removedNode => {
                    const items = queueDestroy.get(removedNode)
                    if (items) {
                        items.forEach(({ cb, args }) => {
                            cb.call(null, args);
                        })
                    }
                    // queueDomRendered.delete(removedNode)
                    // queueDestroy.delete(removedNode);
                    if (removedNode.querySelectorAll) {
                        removedNode.querySelectorAll("*").forEach((current) => {
                            const items = queueDestroy.get(current);
                            if (items) {
                                items.forEach(({ cb, args }) => {
                                    cb.call(null, args);
                                })
                            }
                        })
                    }
                })
                // queueDestroy.forEach(registeredItem => {
                //     const { cb, dom, args } = registeredItem;
                //     if (canRemove(removedNodes, dom)) {
                //         queueDomRendered.delete(dom);
                //         queueDestroy.delete(registeredItem);
                //         const queue = queueDestroy.get(dom);
                //         queue.forEach(cb => {
                //             cb.call(null, args);
                //         })
                //     }
                // })
            }
        }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    const subscribeRendered = (targetDOM, callback, args) => {
        if (document.body.contains(targetDOM)) {
            // setTimeout(() => {
            callback.call(null, args);
            // })
            queueDomRendered.add(targetDOM);
        }
        const newItem = {
            dom: targetDOM,
            cb: callback,
            args
        }
        const queue = queueRender.get(targetDOM) || new Set();
        queue.add(newItem);
        queueRender.set(targetDOM, queue);

        return () => {
            queue.delete(newItem);
        }

    }

    const subscribeDestroyed = (targetDOM, callback, args) => {
        const newItem = {
            dom: targetDOM,
            cb: callback,
            args
        }
        if (document.body.contains(targetDOM)) {
            queueDomRendered.add(targetDOM)
        }
        const queue = queueDestroy.get(targetDOM) || new Set();
        queue.add(newItem);
        queueDestroy.set(targetDOM, queue);
        return () => {
            queue.delete(newItem);
        }
    }

    return [subscribeRendered, subscribeDestroyed];
})();



// export {
//     useSubscribeDOMRendered: ,
//     useSubscribeDOMDestroyed: useSubscribeDOMDestroyed_1
//     useCustomComponentRendered
// }

// export const useSubscribeDOMRendered = useSubscribeDOMRendered_1;
// export const useSubscribeDOMDestroyed = useSubscribeDOMDestroyed_1;

export const useSubscribeDOMRendered = useSubscribeDOMRendered_v2;
export const useSubscribeDOMDestroyed = useSubscribeDOMDestroyed_v2;


export {
    useCustomComponentRendered
}